/**
 * Rám karty hry v knižnici portálu. Portál o hrách nič nevie — obsah (obal,
 * počet hráčov, dĺžku, štítky, rozohrané stoly) dodáva dlaždica hry
 * (`games/<hra>/PortalTile.tsx`), portál len jednotný vzhľad.
 */

import type { CSSProperties, ReactNode } from 'react';

const STATUS_LABEL: Record<Props['status'], string> = {
  live: 'Hrá sa',
  wip: 'Rozpracované',
  planned: 'Pripravujeme',
};

interface Props {
  title: string;
  /** Obal krabice — obrázok alebo kresba, vypĺňa hornú časť karty. */
  cover: ReactNode;
  /** `planned` = na polici, ale hrať sa ešte nedá. */
  status: 'live' | 'wip' | 'planned';
  /** Vlastný text štítku namiesto všeobecného stavu (Unmatched: „Plná verzia 1.0"). */
  badge?: string;
  /**
   * Písmo názvu — každá hra v knižnici vo svojom (Unmatched Syne, Dune Space
   * Mono…). Font dodáva hra, portál ho nepozná.
   */
  titleStyle?: CSSProperties;
  players: string;
  duration: string;
  tags: string[];
  description: string;
  playLabel?: string;
  /** Bez `onPlay` sa hra spustiť nedá (pripravovaná). */
  onPlay?: () => void;
  /** Čo hra ukáže navyše (napr. rozohrané stoly). */
  children?: ReactNode;
}

export function GameCard({
  title,
  cover,
  status,
  badge,
  titleStyle,
  players,
  duration,
  tags,
  description,
  playLabel = 'Spustiť hru',
  onPlay,
  children,
}: Props) {
  return (
    <article className={`libGame libGame--${status}`}>
      <button
        type="button"
        className="libGame__cover"
        onClick={onPlay}
        disabled={!onPlay}
        aria-label={onPlay ? `${playLabel}: ${title}` : title}
      >
        {cover}
        <span className={`libGame__status libGame__status--${status}`}>{badge ?? STATUS_LABEL[status]}</span>
      </button>

      <div className="libGame__body">
        <h2 className="libGame__title" style={titleStyle}>
          {title}
        </h2>
        <p className="libGame__meta">
          <span>
            <PeopleIcon /> {players}
          </span>
          <span>
            <ClockIcon /> {duration}
          </span>
        </p>
        <ul className="libGame__tags">
          {tags.map((tag) => (
            <li key={tag}>{tag}</li>
          ))}
        </ul>
        <p className="libGame__desc">{description}</p>
        {children}
        {onPlay ? (
          <button type="button" className="libGame__play" onClick={onPlay}>
            <PlayIcon /> {playLabel}
          </button>
        ) : (
          <span className="libGame__play" aria-disabled="true">
            Čoskoro na stole
          </span>
        )}
      </div>
    </article>
  );
}

/** Voľné miesto na polici — ďalšia hra ešte nie je. */
export function ShelfSlot() {
  return (
    <div className="libGame libGame--slot" aria-hidden="true">
      <span className="libGame__slotIcon">
        <DieIcon />
      </span>
      <span className="libGame__slotText">Ďalšia hra na stole čoskoro</span>
    </div>
  );
}

function PeopleIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3 19c.8-3.2 3.2-5 6-5s5.2 1.8 6 5" />
      <circle cx="17" cy="9" r="2.4" />
      <path d="M16 14.2c2.3.2 4.1 1.8 4.8 4.8" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
      <path d="M8 5.5v13l10.5-6.5z" />
    </svg>
  );
}

export function DieIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden="true">
      <rect x="3.5" y="3.5" width="17" height="17" rx="4" stroke="currentColor" strokeWidth="2" />
      <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" />
      <circle cx="15.5" cy="8.5" r="1.5" fill="currentColor" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" />
      <circle cx="8.5" cy="15.5" r="1.5" fill="currentColor" />
      <circle cx="15.5" cy="15.5" r="1.5" fill="currentColor" />
    </svg>
  );
}
